import React, { useState } from 'react';
import DashboardLayout from '../../components/includes/DashboardLayout';
import { useStudents, Student } from '../../contexts/StudentsContext';
import AddStudent from './AddStudent';
import ViewStudent from './ViewStudent';
import EditStudent from './EditStudent';
import { useToast } from "../../hooks/use-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import {
  MoreHorizontal,
  Search,
  Plus,
  Users,
  UserPlus,
  GraduationCap,
  TrendingUp,
  FileEdit,
  Trash2,
  Eye,
  Mail,
  Phone
} from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog';

const Students: React.FC = () => {
  const { students, isLoading, deleteStudent } = useStudents();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNiveau, setSelectedNiveau] = useState('all');
  const [selectedStatut, setSelectedStatut] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState<Student | null>(null);

  const niveaux = [
    { id: 'all', name: 'Tous les niveaux' },
    { id: '1', name: '1ère année' },
    { id: '2', name: '2ème année' },
    { id: '3', name: '3ème année' },
    { id: '4', name: '4ème année' }
  ];

  const statuts = [
    { id: 'all', name: 'Tous les statuts' },
    { id: 'actif', name: 'Actif' },
    { id: 'inactif', name: 'Inactif' },
    { id: 'diplome', name: 'Diplômé' },
    { id: 'abandonne', name: 'Abandonné' }
  ];

  const filteredStudents = students.filter(student => {
    const matchesSearch =
      student.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.matricule.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (student.email && student.email.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesNiveau = selectedNiveau === 'all' || student.niveau === selectedNiveau;
    const matchesStatut = selectedStatut === 'all' || student.statut === selectedStatut;
    return matchesSearch && matchesNiveau && matchesStatut;
  });

  // Calculate stats
  const totalStudents = students.length;
  const activeStudents = students.filter(s => s.statut === 'actif').length;
  const graduatedStudents = students.filter(s => s.statut === 'diplome').length;
  const currentYearStudents = students.filter(s => s.anneeAdmission === new Date().getFullYear()).length;

  const stats = [
    {
      title: 'Total Étudiants',
      value: totalStudents.toString(),
      icon: Users,
      color: 'text-blue-600',
      bg: 'bg-blue-100 dark:bg-blue-900/20',
      change: `${activeStudents} actifs`
    },
    {
      title: 'Nouveaux Inscrits',
      value: currentYearStudents.toString(),
      icon: UserPlus,
      color: 'text-green-600',
      bg: 'bg-green-100 dark:bg-green-900/20',
      change: 'Cette année'
    },
    {
      title: 'Diplômés',
      value: graduatedStudents.toString(),
      icon: GraduationCap,
      color: 'text-purple-600',
      bg: 'bg-purple-100 dark:bg-purple-900/20',
      change: 'Total'
    },
    {
      title: 'Taux de Réussite',
      value: totalStudents > 0 ? `${Math.round((graduatedStudents / totalStudents) * 100)}%` : '0%',
      icon: TrendingUp,
      color: 'text-orange-600',
      bg: 'bg-orange-100 dark:bg-orange-900/20',
      change: 'Global'
    }
  ];

  const getStatusBadgeVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case 'actif': return 'default';
      case 'diplome': return 'secondary';
      case 'abandonne': return 'destructive';
      case 'inactif': return 'outline';
      default: return 'outline';
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'actif': return 'Actif';
      case 'diplome': return 'Diplômé';
      case 'abandonne': return 'Abandonné';
      case 'inactif': return 'Inactif';
      default: return status;
    }
  };

  const getInitials = (nom: string, prenom: string) => {
    return `${prenom.charAt(0)}${nom.charAt(0)}`.toUpperCase();
  };

  const handleDeleteStudent = async () => {
    if (!studentToDelete) return;

    try {
      await deleteStudent(studentToDelete.id);
      toast({
        title: "Succès",
        description: "L'étudiant a été supprimé avec succès",
      });
      setShowDeleteDialog(false);
      setStudentToDelete(null);
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.response?.data?.message || "Une erreur est survenue lors de la suppression",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Chargement des étudiants...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
              Gestion des Étudiants
            </h1>
            <p className="text-muted-foreground mt-1">
              Gérez les étudiants inscrits à l'ISTMECH
            </p>
          </div>
          <Button className="w-full md:w-auto" onClick={() => setShowAddModal(true)}>
            <Plus className="mr-2 h-4 w-4" /> Nouvel Étudiant
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <Card key={index}>
              <CardContent className="p-6 flex items-center justify-between space-x-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {stat.title}
                  </p>
                  <div className="flex items-baseline gap-2">
                    <h3 className="text-2xl font-bold">{stat.value}</h3>
                    <span className="text-xs text-green-600 font-medium">
                      {stat.change}
                    </span>
                  </div>
                </div>
                <div className={`p-3 rounded-full ${stat.bg} ${stat.color}`}>
                  <stat.icon className="h-6 w-6" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters & Table Card */}
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <CardTitle>Liste des étudiants</CardTitle>
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder="Rechercher..."
                    className="pl-8 w-full sm:w-[300px]"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                {/* Niveau Filter */}
                <Select value={selectedNiveau} onValueChange={setSelectedNiveau}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder="Filtrer par niveau" />
                  </SelectTrigger>
                  <SelectContent>
                    {niveaux.map((n) => (
                      <SelectItem key={n.id} value={n.id}>
                        {n.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Statut Filter */}
                <Select value={selectedStatut} onValueChange={setSelectedStatut}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder="Filtrer par statut" />
                  </SelectTrigger>
                  <SelectContent>
                    {statuts.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <CardDescription>
              Gérez les informations et le statut des étudiants.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Étudiant</TableHead>
                    <TableHead>Matricule</TableHead>
                    <TableHead>Niveau</TableHead>
                    <TableHead>Filière</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStudents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="h-32 text-center">
                        <div className="flex flex-col items-center justify-center space-y-3">
                          <p className="text-muted-foreground">Aucun étudiant trouvé.</p>
                          {(searchTerm || selectedNiveau !== 'all' || selectedStatut !== 'all') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSearchTerm('');
                                setSelectedNiveau('all');
                                setSelectedStatut('all');
                              }}
                            >
                              Réinitialiser les filtres
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredStudents.map((student) => (
                      <TableRow key={student.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-9 w-9">
                              <AvatarImage src={student.photo} alt={`${student.prenom} ${student.nom}`} />
                              <AvatarFallback>{getInitials(student.nom, student.prenom)}</AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col">
                              <span className="font-semibold">{student.prenom} {student.nom}</span>
                              <span className="text-xs text-muted-foreground">{student.email || 'Pas d\'email'}</span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-xs">{student.matricule}</span>
                        </TableCell>
                        <TableCell>{student.niveau}ème année</TableCell>
                        <TableCell>{student.filiere}</TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(student.statut)} className={
                            student.statut === 'actif' ? 'bg-green-100 text-green-800 hover:bg-green-200 border-none' : ''
                          }>
                            {getStatusLabel(student.statut)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col text-xs text-muted-foreground gap-1">
                            {student.telephone && (
                              <div className="flex items-center gap-1">
                                <Phone className="h-3 w-3" /> {student.telephone}
                              </div>
                            )}
                            {student.email && (
                              <div className="flex items-center gap-1">
                                <Mail className="h-3 w-3" /> {student.email}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 p-0">
                                <span className="sr-only">Ouvrir menu</span>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuItem
                                className="cursor-pointer"
                                onClick={() => {
                                  setSelectedStudent(student);
                                  setShowViewModal(true);
                                }}
                              >
                                <Eye className="mr-2 h-4 w-4" /> Voir
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="cursor-pointer"
                                onClick={() => {
                                  setSelectedStudent(student);
                                  setShowEditModal(true);
                                }}
                              >
                                <FileEdit className="mr-2 h-4 w-4" /> Modifier
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="cursor-pointer text-red-600 focus:text-red-600"
                                onClick={() => {
                                  setStudentToDelete(student);
                                  setShowDeleteDialog(true);
                                }}
                              >
                                <Trash2 className="mr-2 h-4 w-4" /> Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            <div className="text-xs text-muted-foreground mt-4">
              Affichage de <strong>{filteredStudents.length}</strong> sur <strong>{students.length}</strong> étudiants
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add Student Modal */}
      <AddStudent open={showAddModal} onOpenChange={setShowAddModal} />

      {/* View Student Modal */}
      <ViewStudent
        student={selectedStudent}
        open={showViewModal}
        onOpenChange={setShowViewModal}
      />

      {/* Edit Student Modal */}
      <EditStudent
        student={selectedStudent}
        open={showEditModal}
        onOpenChange={setShowEditModal}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer l'étudiant{" "}
              <strong>{studentToDelete?.prenom} {studentToDelete?.nom}</strong> ?
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDeleteStudent}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Students;
